import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Heart, X } from "@phosphor-icons/react";
import { ColorChips } from "@/components/ColorChips";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { API } from "@/lib/api";
import { usePageHead } from "@/lib/siteContent";
import { useWishlist } from "@/lib/wishlist";

const ROWS = [
  ["Type", (i) => i.kind],
  ["Material", (i) => i.material || "—"],
  ["Finish", (i) => i.finish || "—"],
  ["Thickness", (i) => i.thickness || "—"],
  ["Slab size", (i) => (i.sqft > 0 ? i.size : "On request")],
  ["Area", (i) => (i.sqft > 0 ? `${i.sqft} ft²` : "—")],
  ["Brand", (i) => i.brand || "—"],
  [
    "On the floor",
    (i) =>
      i.qty > 0 ? `${i.qty} slab${i.qty === 1 ? "" : "s"}` : "Supplier Stock",
  ],
];

// Side-by-side view of 2–3 slabs picked from the wishlist. Shareable via ?keys=a,b,c
export default function PublicCompare() {
  const site = useSite();
  const [params, setParams] = useSearchParams();
  const wishlist = useWishlist();
  const keys = [
    ...new Set((params.get("keys") || "").split(",").filter(Boolean)),
  ].slice(0, 3);
  const [items, setItems] = useState(null);
  usePageHead({
    title: "Compare stones",
    description:
      "Compare granite, quartz and quartzite slabs side by side — color, material, thickness and finish — at Brioni Granite in St. Louis.",
    suffix: site?.shop?.name,
  });

  useEffect(() => {
    if (!keys.length) {
      setItems([]);
      return;
    }
    let alive = true;
    Promise.all(
      keys.map((k) =>
        fetch(`${API}/public/showroom/item/${k}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => (d ? { ...d.item, slabs: d.slabs } : null))
          .catch(() => null),
      ),
    ).then((list) => alive && setItems(list.filter(Boolean)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("keys")]);

  const remove = (key) =>
    setParams({ keys: keys.filter((k) => k !== key).join(",") });
  const photoUrl = (p) => `${API}/public/showroom/photo/${p}?w=900`;
  const extra = wishlist.filter((w) => !keys.includes(w.key));

  return (
    <SiteShell site={site} testId="site-compare">
      <main className="px-6 pb-20 pt-6 sm:pb-28 sm:pt-8 lg:px-[10vw]">
        <Link
          to="/inventory"
          data-testid="compare-back"
          className="s-micro inline-flex items-center gap-2 text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]"
        >
          <ArrowLeft size={13} weight="bold" /> All inventory
        </Link>
        <h1 className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl">
          Compare slabs
        </h1>
        <p className="mt-3 max-w-xl text-base text-[color:var(--s-mute)]">
          Sizes, finishes and colors side by side. Pick up to three from your
          wishlist.
        </p>

        {items === null ? (
          <div
            className="mt-12 text-sm text-[color:var(--s-mute)]"
            data-testid="compare-loading"
          >
            Loading…
          </div>
        ) : items.length < 2 ? (
          <div className="s-card mt-10 !p-8" data-testid="compare-empty">
            <p className="text-lg font-semibold">
              Pick at least two slabs to compare.
            </p>
            <p className="mt-2 text-sm text-[color:var(--s-mute)]">
              Save slabs with the Wishlist button on any product page, then
              choose “Compare” in your wishlist.
            </p>
            {extra.length > 0 && (
              <div
                className="mt-6 flex flex-wrap gap-2"
                data-testid="compare-suggestions"
              >
                {extra.slice(0, 6).map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    data-testid={`compare-add-${w.key}`}
                    onClick={() =>
                      setParams({ keys: [...keys, w.key].join(",") })
                    }
                    className="s-act s-act-soft"
                  >
                    <Heart size={14} weight="fill" /> {w.color}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-10 overflow-x-auto" data-testid="compare-table">
            <table className="w-full min-w-[640px] border-separate border-spacing-0 text-left">
              <thead>
                <tr>
                  <th className="w-40 align-bottom pb-4 pr-4 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--s-mute)]">
                    Slab
                  </th>
                  {items.map((i) => (
                    <th
                      key={i.key}
                      className="align-bottom pb-4 pr-4"
                      data-testid={`compare-col-${i.key}`}
                    >
                      <div className="s-card relative !p-1.5">
                        <button
                          type="button"
                          data-testid={`compare-remove-${i.key}`}
                          onClick={() => remove(i.key)}
                          aria-label={`Remove ${i.color}`}
                          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center bg-white/90 text-[color:var(--s-ink)] hover:bg-[color:var(--s-ink)] hover:text-white"
                        >
                          <X size={14} weight="bold" />
                        </button>
                        <Link
                          to={`/inventory/${i.key}`}
                          className="s-fig s-card-fig block aspect-[4/3] overflow-hidden"
                        >
                          {i.photo ? (
                            <img
                              src={photoUrl(i.photo)}
                              alt={i.color}
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </Link>
                        <div className="px-3 pb-3 pt-3">
                          <div className="text-xs text-[color:var(--s-mute)]">
                            {i.code}
                          </div>
                          <Link
                            to={`/inventory/${i.key}`}
                            className="mt-0.5 block text-xl font-semibold tracking-tight hover:underline"
                          >
                            {i.color}
                          </Link>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, get]) => (
                  <tr
                    key={label}
                    data-testid={`compare-row-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <th className="border-t border-[#ebe7e0] py-4 pr-4 text-sm font-medium text-[color:var(--s-mute)]">
                      {label}
                    </th>
                    {items.map((i) => (
                      <td
                        key={i.key}
                        className="border-t border-[#ebe7e0] py-4 pr-4 text-base font-medium"
                      >
                        {get(i)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr data-testid="compare-row-colors">
                  <th className="border-t border-[#ebe7e0] py-4 pr-4 text-sm font-medium text-[color:var(--s-mute)]">
                    Colors
                  </th>
                  {items.map((i) => (
                    <td
                      key={i.key}
                      className="border-t border-[#ebe7e0] py-4 pr-4"
                    >
                      <ColorChips
                        primary={i.primary_color}
                        accent={i.accent_color}
                        site
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <th className="border-t border-[#ebe7e0] py-5 pr-4" />
                  {items.map((i) => (
                    <td
                      key={i.key}
                      className="border-t border-[#ebe7e0] py-5 pr-4"
                    >
                      <Link
                        to={`/inventory/${i.key}`}
                        data-testid={`compare-open-${i.key}`}
                        className="s-act s-act-gold"
                      >
                        View &amp; get a quote
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {items && items.length >= 2 && items.length < 3 && extra.length > 0 && (
          <div
            className="mt-8 flex flex-wrap items-center gap-2"
            data-testid="compare-add-more"
          >
            <span className="text-sm text-[color:var(--s-mute)]">
              Add a third:
            </span>
            {extra.slice(0, 5).map((w) => (
              <button
                key={w.id}
                type="button"
                data-testid={`compare-add-${w.key}`}
                onClick={() => setParams({ keys: [...keys, w.key].join(",") })}
                className="s-act s-act-soft"
              >
                <Heart size={14} weight="fill" /> {w.color}
              </button>
            ))}
          </div>
        )}
      </main>
    </SiteShell>
  );
}
